import React, { useState, useCallback } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import Card from './components/Card';
import Button from './components/Button';
import SkeletonCard from './components/shared/SkeletonCard';
import SemrushOverview from './components/SemrushOverview';
import AuditSearchBar from './components/AuditSearchBar';
import AuditEmptyState from './components/AuditEmptyState';
import FindingsList from './components/FindingsList';
import useProjectSynthese from './hooks/useProjectSynthese';
import useAuditFindings from './hooks/useAuditFindings';
import ClientReportExportModal from './components/ClientReportExportModal';
import { useToast } from './ToastContext';
import {
  Server, Code2, Megaphone, Languages, Link2,
  ArrowRight, AlertTriangle, CheckCircle2, Zap, FileText,
  BarChart3, Gauge, Search, RefreshCw, Globe, TrendingUp,
  Download,
} from 'lucide-react';
import { getScoreQualitative } from './utils/scoreLabels';

// ── Score helpers ─────────────────────────────────────────────────────────────

const scoreLabel = getScoreQualitative;

function ScoreRing({ score, size = 68 }) {
  const sw = 7;
  const r = (size - sw) / 2;
  const circ = 2 * Math.PI * r;
  const pct = Math.min(100, Math.max(0, score || 0));
  const offset = circ - (pct / 100) * circ;
  const { ring } = scoreLabel(pct);
  return (
    <svg width={size} height={size} className="-rotate-90" aria-hidden>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={sw} />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke={ring} strokeWidth={sw}
        strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.6s ease' }}
      />
    </svg>
  );
}

function PillarCard({ label, value, description, onClick }) {
  const { label: sl, color, bg } = scoreLabel(value);
  return (
    <button
      onClick={onClick}
      className={`group relative flex flex-col items-center justify-center text-center py-4 px-3 rounded-xl border transition-all hover:shadow-md hover:-translate-y-0.5 cursor-pointer ${bg} border-blue-gray-100 hover:border-accent/30`}
    >
      <div className="relative mb-2">
        <ScoreRing score={value} size={60} />
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="font-bold text-blue-gray-800 text-sm">{value && value > 0 ? Math.round(value) : '—'}</span>
        </div>
      </div>
      <div className="font-semibold uppercase tracking-wider text-blue-gray-500 leading-tight text-[11px]">{label}</div>
      <div className={`font-semibold mt-0.5 text-xs ${color}`}>{sl}</div>
      {description && <div className="text-[10px] text-blue-gray-400 mt-1 leading-snug">{description}</div>}
      <ArrowRight size={12} className="absolute top-2.5 right-2.5 text-blue-gray-300 group-hover:text-accent transition" />
    </button>
  );
}

function InfoBadge({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2 p-3 bg-neutral-50 border border-neutral-200/60 rounded-lg">
      {Icon && <Icon size={14} className="text-blue-gray-400 mt-0.5 flex-shrink-0" />}
      <div className="min-w-0">
        <div className="text-[10px] font-medium text-blue-gray-400 uppercase tracking-wide mb-0.5">{label}</div>
        <div className="text-sm font-medium text-blue-gray-800 leading-tight break-words">{value}</div>
      </div>
    </div>
  );
}

function BlockHeader({ icon: Icon, title, iconClass = 'text-accent', subtitle, children }) {
  return (
    <div className="flex items-start justify-between gap-2 mb-4">
      <div className="flex items-start gap-2">
        <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-accent/10 flex-shrink-0">
          <Icon size={15} className={iconClass} />
        </div>
        <div>
          <h2 className="font-heading font-semibold text-blue-gray-800 text-base leading-tight">{title}</h2>
          {subtitle && <p className="text-xs text-blue-gray-500 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

function MiniStat({ value, label, tone = 'neutral' }) {
  const toneCls = {
    neutral: 'text-blue-gray-800',
    danger: 'text-red-600',
    success: 'text-green-600',
    accent: 'text-accent',
  }[tone];
  return (
    <div className="flex-1 min-w-[90px] rounded-xl border border-blue-gray-100 bg-neutral-50 px-3 py-2.5 text-center">
      <div className={`text-xl font-bold leading-none ${toneCls}`}>{value}</div>
      <div className="text-[11px] font-medium text-blue-gray-500 mt-1 leading-tight">{label}</div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────

export default function ProjectSynthese() {
  const { project, refreshProject } = useOutletContext();
  const navigate = useNavigate();
  const { slug } = useParams();
  const { showToast } = useToast();

  const {
    syntheseData,
    loading,
    error,
    extractingTech,
    extractTechnicalInfoAction,
    reload,
  } = useProjectSynthese(slug, project);

  const { model } = useAuditFindings(slug, project);
  const [exportModalOpen, setExportModalOpen] = useState(false);

  const handleRefresh = useCallback(() => {
    reload();
    if (refreshProject) refreshProject();
  }, [reload, refreshProject]);

  if (loading) {
    return (
      <div className="mx-auto w-full px-2 md:px-0 flex flex-col gap-6">
        <div className="h-28 bg-neutral-100 rounded-2xl animate-pulse" />
        <SkeletonCard variant="metric" count={5} />
        <SkeletonCard variant="text" count={1} title="État des lieux" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <div className="text-center py-12">
          <div className="text-red-600 mb-4">{error}</div>
          <Button variant="primary" onClick={reload}>Réessayer</Button>
        </div>
      </Card>
    );
  }

  if (!syntheseData) return null;

  const km = syntheseData.key_metrics;
  const ti = syntheseData.technical_info;
  const sr = syntheseData.site_recap;
  const sd = project?.semrush_data;

  const scores = model?.scores || {
    global: km.score_global_moyen, seo: km.score_seo_moyen,
    performance: km.score_performance_moyen, technique: km.score_technique_moyen,
    visuel: null, geo: null,
  };
  const verdict = model?.verdict;
  const findings = model?.findings || [];
  const quickWins = model?.quickWins || [];
  const forces = model?.forces || [];
  const counts = model?.counts || { high: 0, medium: 0, total: 0 };
  const topFindings = findings.slice(0, 8);

  const hasNoAudits = km.nb_audits_seo === 0 && km.nb_audits_performance === 0;
  const maillageCoverage = km.pages_sitemap > 0
    ? Math.max(0, Math.min(100, Math.round((km.nb_audits_seo / km.pages_sitemap) * 100)))
    : null;

  return (
    <div className="mx-auto w-full px-2 md:px-0 flex flex-col gap-6">

      {hasNoAudits && (
        <AuditEmptyState
          title="Aucun audit réalisé"
          description="Lancez un audit complet pour obtenir l'état des lieux et les recommandations."
        />
      )}

      {(km.score_penalty_points > 0 || km.sitemap_notice || project?.sitemap_notice) && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="min-w-0 text-sm text-amber-900">
            {km.sitemap_notice || project?.sitemap_notice || (
              <>Une pénalité de <strong>{km.score_penalty_points || project?.score_penalty_points || 0} points</strong> a été appliquée au score global (découverte des URLs limitée).</>
            )}
          </div>
        </div>
      )}

      {/* ═══ 1. VERDICT ════════════════════════════════════════════════════════ */}
      <Card>
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-accent/10 flex-shrink-0">
              <BarChart3 size={15} className="text-accent" />
            </div>
            <h2 className="font-heading font-semibold text-blue-gray-800 text-base">État des lieux</h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <button
              type="button"
              onClick={handleRefresh}
              className="flex items-center gap-1.5 px-2.5 py-2 text-sm text-blue-gray-600 border border-blue-gray-200 rounded-lg hover:bg-blue-gray-50 transition"
              title="Actualiser scores et données"
            >
              <RefreshCw size={14} />
              <span className="hidden md:inline">Actualiser</span>
            </button>
            <AuditSearchBar slug={slug} project={project} className="hidden sm:flex w-52" />
            <button
              type="button"
              disabled={hasNoAudits}
              onClick={() => setExportModalOpen(true)}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-accent rounded-lg hover:bg-accent-dark transition disabled:opacity-50"
              title="Exporter le rapport client en JSON (configurable)"
            >
              <Download size={14} />
              <span className="hidden sm:inline">Exporter rapport (JSON)</span>
            </button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-5">
          <div className="relative flex-shrink-0">
            <ScoreRing score={scores.global} size={104} />
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-bold text-blue-gray-800 leading-none">
                {scores.global > 0 ? Math.round(scores.global) : '—'}
              </span>
              <span className="text-[10px] text-blue-gray-400 uppercase tracking-wide mt-0.5">/ 100</span>
            </div>
          </div>
          <div className="flex-1 min-w-0 text-center sm:text-left">
            {verdict?.label && (
              <span className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full mb-2 ${scoreLabel(scores.global).bg} ${scoreLabel(scores.global).color}`}>
                {verdict.label}
              </span>
            )}
            <p className="text-sm text-blue-gray-700 leading-relaxed">
              {verdict?.text || 'Lancez un audit complet pour établir le verdict du site.'}
            </p>
            <div className="flex gap-2 mt-4 flex-wrap">
              <MiniStat value={km.nb_audits_seo || 0} label="Pages auditées" tone="accent" />
              <MiniStat value={counts.high || 0} label="Points critiques" tone={counts.high > 0 ? 'danger' : 'success'} />
              <MiniStat value={quickWins.length} label="Gains rapides" tone="success" />
            </div>
          </div>
        </div>
      </Card>

      {/* ═══ 2. SCORES PAR PILIER ══════════════════════════════════════════════ */}
      <Card>
        <BlockHeader icon={Gauge} title="Scores par domaine" subtitle="Cliquez un domaine pour voir le détail" />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <PillarCard label="SEO" value={scores.seo} description="Référencement Google" onClick={() => navigate(`/projects/${slug}/seo`)} />
          <PillarCard label="Performance" value={scores.performance} description="Vitesse de chargement" onClick={() => navigate(`/projects/${slug}/performance`)} />
          <PillarCard label="Technique" value={scores.technique} description="HTTPS, indexation…" onClick={() => navigate(`/projects/${slug}/performance`)} />
          <PillarCard label="Visibilité IA" value={scores.geo} description="ChatGPT, Gemini…" onClick={() => navigate(`/projects/${slug}/position`)} />
          <PillarCard label="Visuel" value={scores.visuel} description="Expérience des pages" onClick={() => navigate(`/projects/${slug}/position`)} />
        </div>
      </Card>

      {/* ═══ 3. CONSTATS & PLAN D'ACTION (liste unique priorisée) ══════════════ */}
      {findings.length > 0 && (
        <Card>
          <BlockHeader
            icon={AlertTriangle}
            iconClass="text-red-500"
            title="Ce qui pénalise le plus"
            subtitle={`Du plus prioritaire au moins urgent · ${counts.high || 0} critique(s), ${counts.medium || 0} moyen(s)`}
          >
            {quickWins.length > 0 && (
              <span className="hidden sm:inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full">
                <Zap size={12} /> {quickWins.length} gain(s) rapide(s)
              </span>
            )}
          </BlockHeader>
          <FindingsList findings={topFindings} />
          {findings.length > topFindings.length && (
            <button
              type="button"
              onClick={() => navigate(`/projects/${slug}/errors`)}
              className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-accent hover:underline"
            >
              Voir les {findings.length} constats <ArrowRight size={14} />
            </button>
          )}
        </Card>
      )}

      {/* ═══ 4. FORCES ═════════════════════════════════════════════════════════ */}
      {forces.length > 0 && (
        <Card>
          <BlockHeader icon={CheckCircle2} iconClass="text-green-600" title="Ce qui fonctionne déjà" />
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {forces.map((f, i) => (
              <li key={i} className="flex items-start gap-2 p-3 rounded-lg bg-green-50/60 border border-green-100">
                <CheckCircle2 size={15} className="text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-blue-gray-700 leading-snug">{f}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* ═══ 6. CARTE D'IDENTITÉ DU SITE ═══════════════════════════════════════ */}
      {(() => {
        const hasComprehension = sr?.secteur || sr?.comprehension_ia || sr?.description;
        const hasTechStack = ti?.cms || ti?.hosting || ti?.technology || ti?.framework || ti?.marketing_tools || ti?.is_multilingual;
        if (!hasComprehension && !hasTechStack) return null;
        return (
          <Card>
            <BlockHeader icon={Server} iconClass="text-blue-gray-500" title="Carte d'identité du site" subtitle="Profil, technologies et couverture d'audit">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => extractTechnicalInfoAction(project?.url)}
                disabled={extractingTech}
                className="text-[11px] shrink-0"
              >
                <RefreshCw className={`mr-1 ${extractingTech ? 'animate-spin' : ''}`} size={11} />
                {ti?.hosting ? 'Actualiser' : 'Extraire'}
              </Button>
            </BlockHeader>

            {hasComprehension && (
              <div className="space-y-3 mb-4 pb-4 border-b border-blue-gray-100">
                {sr.secteur && (
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-blue-gray-400 mb-1">Secteur d&apos;activité</div>
                    <div className="text-sm font-medium text-blue-gray-800">{sr.secteur}</div>
                  </div>
                )}
                {sr.comprehension_ia && (
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-blue-gray-400 mb-1">Compréhension IA</div>
                    <p className="text-sm text-blue-gray-700 leading-relaxed">{sr.comprehension_ia}</p>
                  </div>
                )}
                {sr.description && (
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-blue-gray-400 mb-1">Description</div>
                    <p className="text-sm text-blue-gray-700 leading-relaxed">{sr.description}</p>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              <InfoBadge icon={Code2} label="CMS" value={ti?.cms} />
              <InfoBadge icon={Server} label="Hébergeur" value={ti?.hosting} />
              <InfoBadge icon={Code2} label="Technologie" value={ti?.technology || ti?.framework} />
              <InfoBadge icon={Megaphone} label="Outils marketing" value={ti?.marketing_tools} />
              {ti?.is_multilingual && (
                <InfoBadge icon={Languages} label="Multilingue" value={ti?.hreflang_languages || 'Oui'} />
              )}
              {project?.has_llm_file != null && (
                <InfoBadge icon={FileText} label="llm.txt" value={project.has_llm_file ? 'Présent à la racine' : 'Absent'} />
              )}
              <InfoBadge
                icon={FileText}
                label="Sitemap"
                value={km.sitemap_missing ? 'Manquant' : `${km.pages_sitemap || 0} pages`}
              />
              <button
                onClick={() => navigate(`/projects/${slug}/seo`)}
                className="flex items-start gap-2 p-3 bg-neutral-50 border border-neutral-200/60 rounded-lg text-left hover:border-accent/40 transition"
              >
                <Link2 size={14} className="text-blue-gray-400 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-[10px] font-medium text-blue-gray-400 uppercase tracking-wide mb-0.5">Couverture</div>
                  <div className="text-sm font-medium text-blue-gray-800 leading-tight">
                    {maillageCoverage === null ? '—' : `${maillageCoverage}% des pages auditées`}
                  </div>
                </div>
              </button>
            </div>
          </Card>
        );
      })()}

      {/* Données Semrush (rendu autonome) */}
      {sd && (
        <SemrushOverview
          semrushData={sd}
          fetchedAt={project?.semrush_fetched_at}
          slug={slug}
          onRefresh={handleRefresh}
          noKey={false}
        />
      )}

      {/* ═══ 7. ACCÈS DÉTAIL ═══════════════════════════════════════════════════ */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'URL & SEO', icon: Search, to: 'seo' },
          { label: 'Performance & technique', icon: Gauge, to: 'performance' },
          { label: 'Visibilité IA', icon: Globe, to: 'position' },
          { label: 'Pages de référence', icon: TrendingUp, to: 'reference-pages' },
        ].map((item) => (
          <button
            key={item.to}
            onClick={() => navigate(`/projects/${slug}/${item.to}`)}
            className="flex items-center gap-2 px-3 py-3 rounded-xl border border-blue-gray-100 bg-white hover:border-accent/40 hover:bg-accent/5 transition text-left"
          >
            <item.icon size={16} className="text-accent flex-shrink-0" />
            <span className="text-sm font-medium text-blue-gray-700 leading-tight">{item.label}</span>
          </button>
        ))}
      </div>

      <div className="sm:hidden">
        <AuditSearchBar slug={slug} project={project} className="w-full" />
      </div>

      <ClientReportExportModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        slug={slug}
        project={project}
        syntheseData={syntheseData}
        disabled={hasNoAudits}
        onSuccess={() => showToast('Rapport JSON téléchargé.', 'success')}
        onError={(msg) => showToast(msg, 'error')}
      />
    </div>
  );
}
